import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { vendorApi, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Lazy initialization: Check localStorage synchronously on first render
  // This prevents showing "Loading dashboard..." when cached data exists
  const [user, setUser] = useState<User | null>(() => {
    if (vendorApi.isAuthenticated()) {
      return vendorApi.getStoredUser();
    }
    return null;
  });

  // Only show loading if we have a token but NO cached user
  // (rare case: token exists but user data was cleared)
  const [isLoading, setIsLoading] = useState(() => {
    const hasToken = vendorApi.isAuthenticated();
    const hasUser = vendorApi.getStoredUser();
    return hasToken && !hasUser;
  });

  const queryClient = useQueryClient();

  const isAuthenticated = !!user && vendorApi.isAuthenticated();

  useEffect(() => {
    // Only run on initial mount if we're in the rare loading state
    // (token exists but no cached user - need to fetch profile from API)
    if (!isLoading) return;

    const initializeAuth = async () => {
      try {
        if (vendorApi.isAuthenticated()) {
          try {
            const userData = await vendorApi.getProfile();
            localStorage.setItem('vendor_user', JSON.stringify(userData));
            setUser(userData);
          } catch (profileError) {
            console.error('Failed to fetch profile, and no cached data available:', profileError);
            vendorApi.clearToken();
          }
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - isLoading is checked inside

  const login = async (email: string, password: string): Promise<void> => {
    // NOTE: We don't set isLoading here - Login component has its own isSubmitting state
    // Setting isLoading would cause ProtectedRoute to unmount Login component
    try {
      await vendorApi.login({ email, password });

      // Use the stored user data which has merged fields including must_change_password
      const storedUser = vendorApi.getStoredUser();
      if (storedUser) {
        setUser(storedUser);
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    // Clear all React Query cached data FIRST to prevent stale data flash
    queryClient.clear();

    // Clear user state and token together - single render cycle
    setUser(null);
    vendorApi.clearToken();
  };

  const refreshUser = async (): Promise<void> => {
    try {
      if (vendorApi.isAuthenticated()) {
        const userData = await vendorApi.getProfile();
        // Update localStorage with fresh user data
        localStorage.setItem('vendor_user', JSON.stringify(userData));
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // If refresh fails, user might need to login again
      setUser(null);
      vendorApi.clearToken();
    }
  };

  const value: AuthContextType = {
    user,
    setUser,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};